import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getLoggedInUserToken } from "../modules/common/user/UserService";
import { IDLE_LIMIT_MS, REFRESH_AHEAD_MS, isIdle, lastActivityAt, recordActivity, refreshAccessToken } from "./tokenRefresh";

export const SESSION_EXPIRED_PATH = "/session-expired";

/** Expiry of a JWT (its `exp` claim) in epoch millis, or null if the token can't be read. */
export const getTokenExpiryMs = (token: string | null | undefined): number | null => {
  try {
    const payload = token?.split(".")[1];
    if (!payload) return null;
    const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof json.exp === "number" ? json.exp * 1000 : null;
  } catch {
    return null;
  }
};

/** getLoggedInUserToken throws when nobody is logged in; here that just means "no token". */
const readToken = (): string | null => {
  try {
    return getLoggedInUserToken();
  } catch {
    return null;
  }
};

const ACTIVITY_EVENTS = ["mousedown", "keydown", "touchstart", "wheel", "scroll"] as const;

/**
 * Keeps an active user logged in and logs an idle one out:
 * - the 1-hour access token is renewed silently ({@link REFRESH_AHEAD_MS} before it expires) with
 *   the refresh token from login, which lasts 1 day - so a login lasts at most a day;
 * - 45 minutes without a click / key press ({@link IDLE_LIMIT_MS}) ends the session;
 * - if renewing fails, the session ends when the access token expires.
 * Timers don't fire reliably while a laptop sleeps or the tab is in the background, so everything is
 * re-checked when the tab is shown again. If another tab logs out (token removed), this tab follows.
 */
export const useSessionExpiry = () => {
  const navigate = useNavigate();

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let lastExpiresAt: number | null = null;
    let stopped = false;

    const expire = () => navigate(SESSION_EXPIRED_PATH, { replace: true });

    const check = async (): Promise<void> => {
      clearTimeout(timer);
      if (stopped) return;
      const token = readToken();
      if (!token) {
        // removed by another tab: it expired there too, or the user logged out
        const expired = lastExpiresAt !== null && Date.now() >= lastExpiresAt;
        navigate(expired ? SESSION_EXPIRED_PATH : "/logout", { replace: true });
        return;
      }
      if (isIdle()) return expire();

      const expiresAt = getTokenExpiryMs(token);
      lastExpiresAt = expiresAt;
      if (expiresAt === null) return; // unreadable token: the backend's 401 still logs out
      let remaining = expiresAt - Date.now();

      // active and close to expiry: renew, then plan again with the new token
      if (remaining <= REFRESH_AHEAD_MS) {
        if (await refreshAccessToken()) return check();
        remaining = expiresAt - Date.now();
        if (remaining <= 0) return expire();
        timer = setTimeout(check, remaining); // couldn't renew: end at expiry
        return;
      }

      // next look: when it's time to renew, or when the user would become idle
      const untilRefresh = remaining - REFRESH_AHEAD_MS;
      const untilIdle = lastActivityAt() + IDLE_LIMIT_MS - Date.now();
      timer = setTimeout(check, Math.max(1000, Math.min(untilRefresh, untilIdle)));
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };

    check();
    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, recordActivity, { passive: true }));
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", check);
    window.addEventListener("storage", check); // login / logout / renewal / activity in another tab
    return () => {
      stopped = true;
      clearTimeout(timer);
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, recordActivity));
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", check);
      window.removeEventListener("storage", check);
    };
  }, [navigate]);
};
